import React, {Dispatch, FC, useCallback, useEffect, useState} from 'react';
import {Alert, Button, Modal} from "react-bootstrap";
import {FaExpandAlt} from "react-icons/fa";
import UserService from "../../http/UserService";
import Loading from "../Loading";

interface PropsModal {
    visible: boolean;
    setVisible: Dispatch<boolean>;
    userId: string;
    refreshTable: () => void;
}

const UserModalOn: FC<PropsModal> = (props) => {
    const [user, setUser] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);

    const fetchUser = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await UserService.query({
                options: { where: { id: props.userId } }
            });

            if (data && data.length > 0) {
                const userData = data[0];
                if (userData.action === "DELETE") {
                    setError('Пользователь был удалён');
                    setNeedsRefresh(true);
                } else if (userData.active) {
                    setInfo('Пользователь уже включен');
                    setNeedsRefresh(true);
                }

                setUser(userData);
            } else {
                setNeedsRefresh(true);
                setError("Пользователь не найден");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [props.userId]);

    useEffect(() => {
        if (props.visible) {
            void fetchUser();
        }
    }, [props.visible, fetchUser]);

    const handleUserOn = async () => {
        setLoading(true);
        try {
            const { data } = await UserService.update({
                values: { active: true },
                options: { where: { id: user.id } }
            });
            if (data) {
                setSuccess("Пользователь включен");
                setWarning(null);
                setNeedsRefresh(true);
            } else {
                setWarning("Пользователь не изменился");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }

    const handleHideModal = () => {
        if (needsRefresh) props.refreshTable();
        props.setVisible(false);
    }

    return (
        <Modal show={props.visible} size={"lg"} fullscreen={fullscreen ? true : "md-down"} scrollable={true}
               onHide={handleHideModal}>
            <div className="modal-header">
                <h5 className="mb-0">Включение пользователя</h5>
                <div className={"d-flex gap-3"}>
                    <button type="button" className="btn-link" onClick={() => setFullscreen(!fullscreen)}>
                        <FaExpandAlt/>
                    </button>
                    <button type="button" className="btn-close" onClick={handleHideModal}></button>
                </div>
            </div>
            {loading
                ?
                <div style={{height: "200px"}}><Loading/></div>
                :
                <>
                    <Modal.Body>
                        {user
                            ?
                            <>
                                {success && (
                                    <Alert variant={"success"}>
                                        <div><b>{user.email}</b></div>
                                        {success}
                                    </Alert>
                                )}
                                {info && (
                                    <Alert variant={"info"}>
                                        <div><b>{user.email}</b></div>
                                        {info}
                                    </Alert>
                                )}
                                {error && <Alert variant={"danger"}>{error}</Alert>}
                                {!needsRefresh && (
                                    <Alert variant={"warning"}>
                                        <div><b>{user.email}</b></div>
                                        Пользователь будет включен
                                    </Alert>
                                )}
                                {warning && <Alert variant={"warning"}>{warning}</Alert>}
                            </>
                            :
                            <Alert variant={"warning"}>Пользователь не найден</Alert>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {user && !needsRefresh && (
                            <Button variant="outline-success" type="button" onClick={handleUserOn}>Включить</Button>)}
                        <Button variant="secondary" onClick={handleHideModal}>
                            {needsRefresh ? "Закрыть" : "Отмена"}
                        </Button>
                    </Modal.Footer>
                </>
            }
        </Modal>
    );
};

export default UserModalOn;